import { Tag } from '@lastbrand/web-ui/tag'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React from 'react'

import * as styles from './product-price-ds.styles'

// Figma: Quince Design System 2.0 → Widgets → ProductPrice (node 8529:107000).
// Variant names match Figma; prop names follow the price vocabulary used by
// product-info-price-ds (formattedSalePrice / formattedTraditionalPrice / …).
// `single`        — sale price only.
// `singlePromo`   — struck traditional price + red offer price.
// `singlePromoSub`— struck traditional price + black offer price (subscription).
// `servingSub`    — sale price + "(old new /unit)" per-serving breakdown.
// `bundle`        — "From {price}" + struck traditional price, body-20 on all breakpoints.
// `double`        — two segments: sale price + caption, struck traditional price + second caption.
export type ProductPriceDSVariant = 'double' | 'single' | 'singlePromo' | 'singlePromoSub' | 'servingSub' | 'bundle'

// Price fields accept ReactNode so consumers can pass locale-aware nodes
// (e.g. <FormattedPrice/> wrapped in a force-update selector span) as well as plain strings.
export interface ProductPriceDSProps {
  /** Figma `textPrice` — the base/sale price ("$59.75"). */
  formattedSalePrice: React.ReactNode
  /** Figma `textPricePromo` — the price after promo; falls back to formattedSalePrice. singlePromo/singlePromoSub only. */
  formattedOfferPrice?: React.ReactNode
  /** Figma `textPriceOld` — the struck-through compare-at price. singlePromo/singlePromoSub/bundle/double. */
  formattedTraditionalPrice?: React.ReactNode
  /** Figma `textCaption` — first caption under the price. */
  caption?: React.ReactNode
  /** Figma `textCaptionSecond` — caption under the struck price. double only. */
  secondCaption?: React.ReactNode
  /** Figma `hasTag` — cantaloupe Tag content; rendered when present. Not rendered for double. */
  tagLabel?: React.ReactNode
  /** Figma `hasSecondTag` — offwhite Tag content; rendered when present. Not rendered for double. */
  secondTagLabel?: React.ReactNode
  /** Suppresses the localized "from" prefix on the bundle variant (e.g. when the exact price is known). */
  hideFromLabel?: boolean
  /** Per-serving price for servingSub ("$0.55"). */
  formattedPricePerServing?: React.ReactNode
  /** Struck-through per-serving compare-at price for servingSub ("$1.85"). */
  formattedTraditionalPricePerServing?: React.ReactNode
  /** Per-serving unit for servingSub ("serving"). */
  unitForPerServing?: string
  variant?: ProductPriceDSVariant
  className?: string
}

const ProductPriceDS = ({
  formattedSalePrice,
  formattedOfferPrice,
  formattedTraditionalPrice,
  caption,
  secondCaption,
  tagLabel,
  secondTagLabel,
  hideFromLabel,
  formattedPricePerServing,
  formattedTraditionalPricePerServing,
  unitForPerServing,
  variant = 'single',
  className,
}: ProductPriceDSProps) => {
  const { t } = useTranslation('common')
  const isBundle = variant === 'bundle'
  const isPromo = variant === 'singlePromo' || variant === 'singlePromoSub'
  const showServing = variant === 'servingSub' && !!formattedPricePerServing && !!unitForPerServing

  // <s> is decorative to screen readers; this hint gives the struck price a "was" reading.
  const strikeHint = <span className={styles.srOnlyCss}>{t('originally')}</span>

  if (variant === 'double') {
    return (
      <div data-testid="product-price" className={cx(styles.containerDoubleCss, className)}>
        <div className={styles.segmentCss}>
          <p className={styles.priceCss}>{formattedSalePrice}</p>
          {caption && <p className={styles.captionCss}>{caption}</p>}
        </div>
        {formattedTraditionalPrice && (
          <div className={styles.segmentCss}>
            <s className={styles.strikePriceCss}>
              {strikeHint}
              {formattedTraditionalPrice}
            </s>
            {secondCaption && <p className={styles.captionCss}>{secondCaption}</p>}
          </div>
        )}
      </div>
    )
  }

  return (
    <div data-testid="product-price" className={cx(styles.containerCss, className)}>
      <div className={styles.segmentGrowCss}>
        <div className={styles.wrapperCss}>
          {isBundle || isPromo || showServing ? (
            <div className={styles.headerRowCss}>
              {isPromo && formattedTraditionalPrice && (
                <s className={styles.strikePriceCss}>
                  {strikeHint}
                  {formattedTraditionalPrice}
                </s>
              )}
              {isBundle ? (
                <p className={styles.priceBundleCss}>
                  {!hideFromLabel && <span>{t('from')}</span>}
                  <span>{formattedSalePrice}</span>
                </p>
              ) : (
                <p className={variant === 'singlePromo' ? styles.offerPricePromoCss : styles.priceCss}>
                  {isPromo ? (formattedOfferPrice ?? formattedSalePrice) : formattedSalePrice}
                </p>
              )}
              {showServing && (
                <span className={styles.servingCss}>
                  {'('}
                  <span className={styles.servingWrapperCss}>
                    {formattedTraditionalPricePerServing && (
                      <s className={styles.servingStrikePriceCss}>
                        {strikeHint}
                        {formattedTraditionalPricePerServing}
                      </s>
                    )}
                    <span>{formattedPricePerServing}</span>
                  </span>
                  /{unitForPerServing}
                  {')'}
                </span>
              )}
              {isBundle && formattedTraditionalPrice && (
                <s className={styles.strikePriceBundleCss}>
                  {strikeHint}
                  {formattedTraditionalPrice}
                </s>
              )}
            </div>
          ) : (
            <p className={styles.priceCss}>{formattedSalePrice}</p>
          )}
          {/* independent boolean checks — `??` would let an empty-string tagLabel suppress the second tag */}
          {(!!tagLabel || !!secondTagLabel) && (
            <div className={styles.tagsCss}>
              {tagLabel && <Tag variant="cantaloupe">{tagLabel}</Tag>}
              {secondTagLabel && <Tag variant="offwhite">{secondTagLabel}</Tag>}
            </div>
          )}
        </div>
        {caption && <p className={styles.captionCss}>{caption}</p>}
      </div>
    </div>
  )
}

export default ProductPriceDS
